import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Tooltip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Check, Notifications, Close } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';
import { isArray, isNull } from 'lodash';
import PropTypes from 'prop-types';

import { UnidataTable, Modal } from '~/components';
import { MODULO_ALARMES } from '~/constants/modules';
import { t, number } from '~/helpers';
import api from '~/services/api';
import { recordFiltering } from '~/store/modules/alarmesSistemas/actions';

import RecognizeComponent from './components/Recognize';
import RecognizeUnityComponent from './components/RecognizeUnity';
import * as S from './styles';

export default function BadgeAlert({ time }) {
  const [alerts, setAlerts] = useState(0);
  const [open, setOpen] = useState(false);

  const theme = useTheme();
  const dispatch = useDispatch();

  const recognize = useRef(null);
  const recognizeUnity = useRef(null);

  const { modules } = useSelector((state) => state.user);
  const alarmes = useSelector((state) => state.alarmesSistemas);

  const handleColor = useCallback(
    ({ datahorareconhecimentotbalarmesistema }) => {
      if (isNull(datahorareconhecimentotbalarmesistema))
        return { color: theme.palette.error.main };

      return {};
    },
    []
  );

  const header = useMemo(
    () => [
      {
        id: 'datahoraocorrenciatbalarmesistema',
        label: t('s.geral.alertasistema.datahoraocorrenciatbalarmesistema'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        styled: (value) => handleColor(value),
      },
      {
        id: 'codigolabelsistema',
        label: t('s.geral.alertasistema.codigolabelsistema'),
        formatted: (value) => t(value),
        styled: (value) => handleColor(value),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeabreviadoposto',
        label: t('s.geral.alertasistema.nomeabreviadoposto'),
        styled: (value) => handleColor(value),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigousuario',
        label: t('s.geral.alertasistema.codigousuario'),
        styled: (value) => handleColor(value),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'origemalarmesistema',
        label: t('s.geral.alertasistema.origemalarmesistema'),
        styled: (value) => handleColor(value),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'mensagemalarmesistema',
        label: t('s.geral.alertasistema.mensagemalarmesistema'),
        styled: (value) => handleColor(value),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'tipocalculotbalarmesistema',
        label: t('s.geral.alertasistema.tipocalculotbalarmesistema'),
        styled: (value) => handleColor(value),
      },
      {
        id: 'valormomentotbalarmesistema',
        label: t('s.geral.alertasistema.valormomentotbalarmesistema'),
        formatted: (value) => (!isNull(value) ? number(value, 2) : ''),
        styled: (value) => handleColor(value),
      },
      {
        id: 'valorreferenciatbalarmesistema',
        label: t('s.geral.alertasistema.valorreferenciatbalarmesistema'),
        formatted: (value) => (!isNull(value) ? number(value, 1) : ''),
        styled: (value) => handleColor(value),
      },
      {
        id: 'valorpercentualalarmesistema',
        label: t('s.geral.alertasistema.valorpercentualalarmesistema'),
        formatted: (value) => (!isNull(value) ? number(value, 1) : ''),
        styled: (value) => handleColor(value),
      },
    ],
    [t('s.geral.alertasistema.valorreferenciatbalarmesistema')]
  );

  const buttons = useMemo(
    () => [
      {
        title: t('s.geral.alertasistema.reconheceralarme'),
        icon: <Check />,
        action: (data) => recognizeUnity.current.handleOpenModal({ data }),
        color: 'primary',
      },
    ],
    []
  );

  const handleGetAPI = useCallback(async () => {
    try {
      const { data } = await api.get('alarmessistemas/ativos');
      setAlerts(data.alarmes);
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  const handleGetDispatchRedux = useCallback((query) => {
    dispatch(recordFiltering(query));
  }, []);

  const handleAcknowledgeAllAlarms = useCallback(() => {
    recognize.current.handleOpenModal();
  }, []);

  useEffect(() => {
    if (modules.includes(MODULO_ALARMES)) {
      handleGetAPI();

      const isTime = setInterval(() => {
        handleGetAPI();
      }, time);

      return () => clearInterval(isTime);
    }
  }, [modules]);

  useEffect(() => {
    if (open) {
      const isTime = setInterval(() => {
        handleGetDispatchRedux(alarmes.params);
      }, time);

      return () => clearInterval(isTime);
    }
  }, [open, alarmes.params]);

  if (modules.includes(MODULO_ALARMES))
    return (
      <>
        <Tooltip title={t('c.menu.alarme.titulo')}>
          <IconButton color="inherit">
            <S.WrapperBadge
              color="secondary"
              badgeContent={alerts}
              onClick={() => setOpen(true)}
            >
              <Notifications />
            </S.WrapperBadge>
          </IconButton>
        </Tooltip>

        <Dialog fullScreen open={open} onClose={() => setOpen(false)}>
          <AppBar>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setOpen(false)}
              >
                <Close />
              </IconButton>
              <Typography variant="h6">
                {t('s.configuracao.alarmesistema')}
              </Typography>
            </Toolbar>
          </AppBar>

          <S.DrawerHeader>
            <Button
              color="primary"
              variant="contained"
              onClick={handleAcknowledgeAllAlarms}
            >
              {t('s.geral.alertasistema.reconhecertodos')}
            </Button>
          </S.DrawerHeader>

          <S.WrapperContent>
            <UnidataTable
              fixed
              header={header}
              redux={alarmes}
              read={recordFiltering}
              keys="idalarmesistema"
              create={false}
              update={false}
              remove={false}
              actions={buttons}
            />
          </S.WrapperContent>
        </Dialog>

        <Modal
          form
          disableBackdropClick
          disableEscapeKeyDown
          ref={recognize}
          component={RecognizeComponent}
          maxWidth="xs"
          labelConfirm="s.geral.opcoes.sim"
          labelCancell="s.geral.opcoes.nao"
          title={t('s.geral.alertasistema.reconhecertodos')}
          onForm={() => {
            handleGetDispatchRedux(alarmes.params);
            handleGetAPI();
          }}
          noPermission
        />

        <Modal
          form
          disableBackdropClick
          disableEscapeKeyDown
          ref={recognizeUnity}
          component={RecognizeUnityComponent}
          maxWidth="xs"
          labelConfirm="s.geral.opcoes.sim"
          labelCancell="s.geral.opcoes.nao"
          title={t('s.geral.alertasistema.reconheceralarme')}
          onForm={() => {
            handleGetDispatchRedux(alarmes.params);
            handleGetAPI();
          }}
          noPermission
        />
      </>
    );

  return <div />;
}

BadgeAlert.propTypes = {
  time: PropTypes.number,
};

BadgeAlert.defaultProps = {
  time: 60000,
};
